import { Send } from "lucide-react";
import { useEffect } from "react";
import { useFormContext, useWatch, get, useFormState, UseFormReturn } from "react-hook-form";
import { useGetAllFedexAccounts } from "@/api/fedexAccount.api";
import { buildShippingReference } from "@/components/backOffice/expeditionForm/expeditionFormHelper";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledUnifiedDatePicker } from "@/components/ui/customs/controlled-unified-date-picker";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { FedexPackagingEnum } from "@/constants/enums/fedexPackaging.enum";
import { FedexSignatureEnum } from "@/constants/enums/fedexSignature.enum";
import { GetAllCompanyDto, GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";
import { FedexApiPackage } from "./fedex/fedexApiPackage";
import { FedexApiRates } from "./fedex/fedexApiRates";
import { ShipperInfo } from "./shipperInfo";
import { ManufacturerInfo } from "./manufacturerInfo";
import { getDestinationRegion } from "../deliveryContent/deliveryContent.utils";
import { toStartOfDay } from "@/lib/utils";
import { useFedexPackagingOptions } from "@/hooks/useEnumOptions";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import { useTranslation } from "react-i18next";

const PACKAGING_FIELD_NAME = "expeditionFedexApi.expeditionPackaging" as const;

export const ApiInfo = ({
  expedition,
  company,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const translateMessage = useTranslateMessage();
  const fedexPackagingOptions = useFedexPackagingOptions();
  // Ici on utilise quotation form mais c'est aussi utilisé par customquotation donc erreur potentielle
  const methods = useFormContext<QuotationUpdateBoFormData>();
  const { data: fedexAccounts } = useGetAllFedexAccounts();
  const { errors } = useFormState({
    control: methods.control,
    name: PACKAGING_FIELD_NAME,
  });
  const packagingError = get(errors, PACKAGING_FIELD_NAME);
  const packagingErrorMessage = packagingError?.message ?? packagingError?.root?.message;

  const destinationRegion = getDestinationRegion(expedition?.deliveryInfo?.country);

  const apiEnum = useWatch({
    control: methods.control,
    name: "apiEnum",
  });
  const signatureOption = useWatch({
    control: methods.control,
    name: "options.signature",
  });

  useEffect(() => {
    if (destinationRegion === "UE" && apiEnum === "NONE") {
      methods.setValue("expeditionWoApi.packageCount", 1);
      methods.setValue("expeditionWoApi.weight", 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationRegion, apiEnum]);

  useEffect(() => {
    if (apiEnum !== "FEDEX") return;

    const nextSignatureType = signatureOption
      ? FedexSignatureEnum.ADULT_SIGNATURE
      : FedexSignatureEnum.NONE;

    methods.setValue("expeditionFedexApi.signatureType", nextSignatureType, { shouldDirty: true });
  }, [apiEnum, signatureOption, methods]);

  if (apiEnum === "NONE") {
    return (
      <AccordionItem
        value="apiInfo"
        className="shadow-basic flex w-full flex-col items-start justify-center gap-4 rounded-xl border border-gray-200 bg-slate-100 p-6"
      >
        <AccordionTrigger className="flex w-full flex-row items-start justify-between gap-2 self-start !no-underline">
          <div className="flex flex-row items-center gap-2 self-start">
            <Send className="h-6 w-6 !rotate-0 text-blue-600" />
            <p className="text-xl font-bold">{t("apiInfo.title")}</p>
          </div>
        </AccordionTrigger>
        <AccordionContent forceMount className="flex w-full flex-col gap-2">
          <div className="flex w-full flex-col gap-3">
            <ShipperInfo expedition={expedition} company={company} />
            <div className="flex flex-col gap-1">
              <ControlledCheckbox
                control={methods.control}
                name="options.handDelivery"
                label={t("apiInfo.handDeliveryLabel")}
              />
              <p className="text-xs text-gray-500">{t("apiInfo.handDeliveryHelp")}</p>
            </div>
            {/* // TODO : add back when remote pickup is implemented */}
            {/* <ControlledSwitch
              control={methods.control}
              name="options.remotePickup"
              label="Enlèvement à distance"
              className="w-10"
            /> */}
          </div>
          <div className="my-2 w-full border-t-2 border-dashed border-slate-300" />
          <h3 className="text-lg font-bold">{t("apiInfo.packagesTitle")}</h3>
          <div className="flex w-full flex-col gap-2 sm:flex-row">
            <ControlledUnifiedInput
              label={t("apiInfo.packageCountLabel")}
              required
              control={methods.control}
              name="expeditionWoApi.packageCount"
              inputType="integer"
              className="w-full"
              wrapperClassName="w-full"
            />
            <ControlledUnifiedInput
              label={t("apiInfo.totalWeightLabel")}
              required
              control={methods.control}
              name="expeditionWoApi.weight"
              inputType="float"
              className="w-full"
              wrapperClassName="w-full"
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  }

  return (
    <AccordionItem
      value="apiInfo"
      className="shadow-basic flex w-full flex-col items-start justify-center gap-4 rounded-xl border border-gray-200 bg-slate-100 p-6"
    >
      <AccordionTrigger className="flex w-full flex-row items-start justify-between gap-2 self-start !no-underline">
        <div className="flex flex-row items-center gap-2 self-start">
          <Send className="h-6 w-6 !rotate-0 text-blue-600" />
          <p className="text-xl font-bold">{t("apiInfo.title")}</p>
        </div>
      </AccordionTrigger>
      <AccordionContent forceMount className="flex w-full flex-col gap-2">
        <div className="grid grid-cols-1 gap-2 @3xl:grid-cols-2">
          <ControlledUnifiedDatePicker
            label={t("apiInfo.shipDateLabel")}
            required
            control={methods.control}
            name="expeditionFedexApi.shipDate"
            disabledDates={(date) => {
              const startOfToday = toStartOfDay(new Date());
              const maxDate = new Date(startOfToday);
              maxDate.setDate(maxDate.getDate() + 10);
              return date < startOfToday || date > maxDate;
            }}
          />
          <ReferenceInput expedition={expedition} company={company} methods={methods} />
          <ShipperInfo expedition={expedition} company={company} />
          <ManufacturerInfo company={company} />
          {/* // TODO : add back when remote pickup is implemented */}
          {/* <ControlledSwitch
            control={methods.control}
            name="options.remotePickup"
            label="Enlèvement à prévoir"
            className="@3xl:col-span-2 w-fit"
          /> */}
          <ControlledSelectV2
            label={t("apiInfo.packagingTypeLabel")}
            required
            options={fedexPackagingOptions}
            control={methods.control}
            name="expeditionFedexApi.packagingType"
            wrapperClassName="@3xl:col-span-2"
            defaultValue={FedexPackagingEnum.YOUR_PACKAGING as never}
          />
        </div>
        <div className="my-2 w-full border-t-2 border-dashed border-slate-300" />
        <h3 className="text-lg font-bold">{t("apiInfo.packagesTitle")}</h3>
        <FedexApiPackage />
        {packagingErrorMessage && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-amber-800">
            <p className="text-amber-800">{translateMessage(packagingErrorMessage)}</p>
          </div>
        )}
        <div className="my-2 w-full border-t-2 border-dashed border-slate-300" />
        <h3 className="text-lg font-bold">{t("apiInfo.ratesTitle")}</h3>
        <FedexApiRates fedexAccounts={fedexAccounts ?? []} expedition={expedition} />
      </AccordionContent>
    </AccordionItem>
  );
};

const ReferenceInput = ({
  expedition,
  company,
  methods,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  methods: UseFormReturn<QuotationUpdateBoFormData>;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const isPriority = useWatch({
    control: methods.control,
    name: "options.express",
  });
  const woodbox = useWatch({
    control: methods.control,
    name: "quotationWoodBox",
  });
  const deliveryContent = useWatch({
    control: methods.control,
    name: "deliveryContent",
  });
  const billingEntity = company.billingEntities.find(
    (billingEntity) => billingEntity.id === expedition?.expeditionBillingDetail?.billingEntityId,
  );

  const handleRefreshShippingReference = () => {
    const expeditionNumber =
      expedition?.quotation?.identifier || expedition?.customQuotation?.identifier;
    const totalBottleCount = deliveryContent.reduce((acc, curr) => acc + curr.numberOfBottles, 0);
    const shippingReference = buildShippingReference({
      expeditionNumber: expeditionNumber || "",
      companyName: billingEntity?.internalName || company.businessName,
      totalBottleCount,
      woodbox: woodbox && woodbox.length > 0,
      isPriority,
      isReExpedition: !!expedition?.basedOnId,
    });
    methods.setValue("expeditionFedexApi.shippingReference", shippingReference);
  };

  useEffect(() => {
    handleRefreshShippingReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPriority, woodbox, deliveryContent]);

  return (
    <div className="flex flex-row gap-2">
      <ControlledUnifiedInput
        label={t("apiInfo.shippingReferenceLabel")}
        control={methods.control}
        name="expeditionFedexApi.shippingReference"
        inputType="text"
        wrapperClassName="w-full"
        disabled
      />
    </div>
  );
};
